import React, { useMemo, useState } from 'react';
import _debounce from 'lodash/debounce';
import { useHistory } from 'react-router-dom';

import { PersonAddIcon } from 'components/Icons';
import TableFilterControls, {
  FilterByOption,
  FilterSortOption
} from 'components/TableFilterControls/TableFilterControls';

import {
  DEFAULT_FILTER,
  DEFAULT_SORT,
  MOBILE_SORT_OPTIONS,
  SUBSCRIBER_FILTER_OPTIONS
} from './LabelSubscriberOptions';
import { useLabelSubscribersContext } from './LabelSubscribersContext';

const LabelSubscribersFilterControls = () => {
  const {
    searchText,
    setUrlQuery,
    sortValue,
    fetching,
    pageInfo,
    filter: subscribersFilter,
    isSmallScreen
  } = useLabelSubscribersContext();
  const history = useHistory();
  const totalCount = pageInfo ? pageInfo.nodeCount : 0;

  const [searchInputText, setSearchInputText] = useState(searchText || '');

  const displayLoading = fetching && totalCount === 0;
  const searchPlaceholder = `Search ${totalCount} Subscriber${
    totalCount === 1 ? '' : 's'
  }`;

  const debouncedChangeHandler = useMemo(() => {
    const searchChangeHandler = (value: string) => setUrlQuery({ q: value, page: 1 });
    return _debounce(searchChangeHandler, 350);
  }, [setUrlQuery]);

  const showAddNewSubscriber = () => {
    history.push(`${location.pathname}/add`);
  };

  return (
    <TableFilterControls
      mobile={isSmallScreen}
      withinContainer
      searchInput={{
        value: searchInputText,
        disabled: totalCount === 0 && fetching,
        placeholder: displayLoading ? 'Loading...' : searchPlaceholder,
        'data-testid': 'LabelSubscribers-search',
        onChange: e => {
          const value = e.target.value;
          setSearchInputText(value);
          debouncedChangeHandler(value);
        }
      }}
      clearFilters={() => {
        setUrlQuery({
          filter: DEFAULT_FILTER.value,
          page: 1,
          // NOTE: We only consider sort a filter in the mobile view because it lives
          // in a dropdown in the mobile UI
          sort: isSmallScreen ? DEFAULT_SORT.id : undefined,
          dir: isSmallScreen ? DEFAULT_SORT.direction : undefined
        });
      }}
      filters={[
        {
          name: 'subscription-type',
          testId: 'LabelSubscribers-filter-dropdown',
          options: SUBSCRIBER_FILTER_OPTIONS,
          // 'data-testid': Subscriptions.TEST_IDS.FILTER_SELECT,
          style: { minWidth: '12rem' },
          onChange: (option: FilterByOption) => {
            if (option.value === subscribersFilter) return;
            setUrlQuery({ page: 1, filter: option.value });
          },
          value: SUBSCRIBER_FILTER_OPTIONS.find(
            ({ value }) => value === subscribersFilter
          )!,
          active: subscribersFilter !== DEFAULT_FILTER.value
        },
        isSmallScreen && {
          name: 'sort',
          sort: true,
          options: MOBILE_SORT_OPTIONS,
          testId: 'LabelSubscribers-sort-dropdown',
          style: { minWidth: '15rem' },
          onChange: (option: FilterSortOption) => {
            if (option.value === sortValue) return;
            setUrlQuery({ page: 1, sort: option.id, dir: option.direction });
          },
          value: MOBILE_SORT_OPTIONS.find(({ value }) => value === sortValue)!,
          active: sortValue !== DEFAULT_SORT.value
        }
      ].filter(Boolean)}
      actions={[
        {
          name: 'add-subscriber',
          label: (
            <>
              <PersonAddIcon color="currentColor" size={14} style={{ marginRight: 6 }} />{' '}
              {isSmallScreen ? 'Invite' : 'New DJ Invite'}
            </>
          ),
          'data-testid': 'AddSubscriberButton',
          onClick: showAddNewSubscriber
        }
      ]}
    />
  );
};

export default LabelSubscribersFilterControls;
